import { ExportItemSetFormat, useExportItemSetMutation } from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormRadioSelection,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import { ITEM_SET_EXPORT_FILE_TEMPLATE_OPTIONS, ItemSetExportFileTemplate } from '../../constants';

import {
  type ItemSetExportFormInput,
  ItemSetExportFormSchema,
} from './item-set-export-form-schema';

interface ItemSetExportFormModalProps {
  open: boolean;
  onClose: () => void;
  questionGroupId: string;
}

export default function ItemSetExportFormModal(props: Readonly<ItemSetExportFormModalProps>) {
  const { open: isOpen, onClose, questionGroupId } = props;

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<ItemSetExportFormInput>({
    defaultValues: {
      fileType: ExportItemSetFormat.Pdf,
      template: ItemSetExportFileTemplate.QUESTION_WITH_CHOICES,
    },
    resolver: zodResolver(ItemSetExportFormSchema),
  });

  const [exportItemSet] = useExportItemSetMutation();

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = formContext;

  const onSubmit = async (data: ItemSetExportFormInput) => {
    const hasSolution =
      data.template === ItemSetExportFileTemplate.QUESTION_WITH_CHOICES_AND_SOLUTION;

    await exportItemSet({
      variables: {
        input: {
          questionGroupId,
          format: data.fileType,
          includeSolution: hasSolution,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'Export Successfully',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'Export Failed',
          variant: 'error',
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm'>
      <FormProvider {...formContext}>
        <Form
          onSubmit={(event) => {
            event.stopPropagation();
            handleSubmit(onSubmit)(event);
          }}
        >
          <DialogTitle onClose={onClose} loading={isSubmitting}>
            Export Item Set
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              {/* TODO: Uncomment this when exporting to DOCX is ready
              <FormRadioSelection
                name='fileType'
                disabled={isSubmitting}
                options={ITEM_SET_EXPORT_FILE_TEMPLATE_OPTIONS}
                label='ประเภทไฟล์'
                row
              /> */}
              <FormRadioSelection
                name='template'
                disabled={isSubmitting}
                options={ITEM_SET_EXPORT_FILE_TEMPLATE_OPTIONS}
                label='รายละเอียด'
                row
              />
            </Flex>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button
              disabled={isSubmitting}
              variant='outlined'
              color='secondary'
              size='large'
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button loading={isSubmitting} type='submit' size='large'>
              Export
            </Button>
          </DialogActions>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
